/** ICP-000002 — Enterprise Repository Blueprint (mirrors pkg/repositoryblueprint) */

export const ICP_REPO_ID = 'ICP-000002';

export interface TeamMemberRow {
  role: string;
  responsibility: string;
  focus: string;
}

export interface RootAreaRow {
  path: string;
  purpose: string;
  owner: string;
}

export interface BackendDomainRow {
  domain: string;
  path: string;
  dependencies: string;
  publicApi: string;
}

export interface FrontendComponentRow {
  tier: string;
  path: string;
  examples: string;
  consumers: string;
}

export interface DatabaseSchemaRow {
  schema: string;
  purpose: string;
  owner: string;
  tables: number;
}

export interface AIMarketplaceRow {
  area: string;
  path: string;
  modules: number;
  status: string;
}

export interface SharedInfraRow {
  category: string;
  path: string;
  items: number;
  status: string;
}

export interface ExecutiveRepositoryRow {
  metric: string;
  value: string;
  status: string;
  score: number;
}

export const REPOSITORY_TEAM = [
  'Chief Enterprise Architect', 'Chief Technology Officer', 'Backend Architecture Lead',
  'Frontend Architecture Lead', 'AI Architecture Lead', 'Database Architect',
  'DevOps Architect', 'Security Architect', 'QA Architect', 'Documentation Architect',
];

export const REPOSITORY_ROOT = [
  'services/', 'pkg/', 'adapters/', 'apps/', 'packages/', 'infra/', 'docs/', 'tests/',
  'tools/', 'scripts/', '.github/', '.vscode/', '.devcontainer/',
];

export const BACKEND_STRUCTURE = [
  'cmd/', 'internal/', 'bootstrap/', 'config/', 'api/', 'middleware/', 'handler/',
  'service/', 'repository/', 'domain/', 'dto/', 'events/', 'worker/', 'audit/', 'security/',
];

export const BACKEND_DOMAINS = [
  'Identity', 'Organization', 'Tenant', 'Workspace', 'Users', 'Roles', 'Permissions',
  'Products', 'Inventory', 'Orders', 'Marketplace', 'Finance', 'Advertising',
  'Analytics', 'Automation', 'Knowledge', 'AI', 'Customer Success', 'Administration',
];

export const FRONTEND_STRUCTURE = [
  'app/', 'layouts/', 'pages/', 'components/', 'features/', 'hooks/', 'contexts/',
  'providers/', 'stores/', 'services/', 'api/', 'auth/', 'router/', 'themes/', 'types/',
];

export const COMPONENT_LIBRARY_TIERS = [
  'Base Components', 'Composite Components', 'Business Components', 'AI Components',
  'Marketplace Components', 'Dashboard Components', 'Admin Components',
];

export const DATABASE_STRUCTURE = [
  'migrations/', 'rollback/', 'seed/', 'views/', 'functions/', 'extensions/',
  'materialized-views/', 'indexes/', 'partitions/', 'audit/',
];

export const POSTGRES_SCHEMAS = [
  'core', 'identity', 'commerce', 'inventory', 'marketplace', 'advertising', 'finance',
  'analytics', 'knowledge', 'automation', 'ai', 'notifications', 'audit', 'system',
  'reporting', 'admin',
];

export const AI_STRUCTURE = [
  'prompt-engine/', 'dna-engine/', 'knowledge-engine/', 'agents/', 'memory/', 'models/',
  'validation/', 'evaluation/', 'reasoning/', 'learning/', 'guardrails/',
];

export const MARKETPLACE_STRUCTURE = [
  'amazon/', 'flipkart/', 'myntra/', 'ajio/', 'shopify/', 'common/', 'oauth/', 'webhooks/',
  'catalog/', 'inventory/', 'orders/', 'advertising/', 'settlements/',
];

export const SHARED_LIBRARIES = [
  'events/', 'contracts/', 'models/', 'types/', 'constants/', 'helpers/', 'validation/',
  'security/', 'logging/', 'metrics/',
];

export const INFRASTRUCTURE = [
  'docker/', 'nginx/', 'redis/', 'postgres/', 'monitoring/', 'grafana/', 'prometheus/',
  'backup/', 'restore/', 'certificates/',
];

export const DOCUMENTATION = [
  'architecture/', 'api/', 'database/', 'deployment/', 'coding/', 'testing/',
  'marketplace/', 'ai/', 'governance/', 'runbooks/', 'adr/',
];

export const TESTING = [
  'unit/', 'integration/', 'contract/', 'performance/', 'security/', 'playwright/',
  'fixtures/', 'mocks/', 'helpers/',
];

export const TOOLS = [
  'generators/', 'linters/', 'codegen/', 'migration-tools/', 'benchmark/', 'profiling/', 'diagnostics/',
];

export const CONFIGURATION = [
  'development/', 'testing/', 'staging/', 'production/', 'feature-flags/', 'environment/',
];

export const IMPORT_RULES = [
  'Allowed Imports', 'Forbidden Imports', 'Cross-Domain Rules', 'Dependency Direction',
  'Layer Isolation', 'Marketplace Adapter Only', 'No Core Marketplace Logic',
];

export const BUILD_RULES = [
  'Backend Build', 'Frontend Build', 'Docker Build', 'Documentation Build',
  'Testing Build', 'Release Build',
];

export const OWNERSHIP_FIELDS = ['Owner', 'Purpose', 'Dependencies', 'Allowed Consumers', 'Public Interfaces'];

export const NAMING_CONVENTIONS = [
  'Service Naming', 'Package Naming', 'Migration Naming', 'Event Naming',
  'Component Naming', 'Schema Naming', 'API Naming',
];

export const FUTURE_ICP_POINTS = [
  'ICP-000001 Coding Constitution', 'ICP-000003 API Contracts', 'ICP-000004 Event Catalog',
  'ICP-000005 Database Standards', 'ICP-000006 CI/CD Pipeline',
];

export const ARCHITECTURE_STAGES = [
  'Repository Team', 'Repository Root', 'Backend Domains', 'Frontend Components',
  'Database Schemas', 'AI Marketplace', 'Shared Infra Config', 'Executive Repository',
];

export const LIVE_ROUTES: Record<string, string> = {
  codingConstitution: '/platform/coding-constitution',
  masterDependencyMatrix: '/platform/master-dependency-matrix',
  enterpriseDevOps: '/platform/enterprise-devops',
  engineeringGovernance: '/platform/engineering-governance',
};

export const DEMO_TEAM: TeamMemberRow[] = [
  { role: 'Chief Enterprise Architect', responsibility: 'Repository structure approval', focus: 'Domain boundaries' },
  { role: 'Backend Architecture Lead', responsibility: 'Backend folder and service layout', focus: 'Clean Architecture layers' },
  { role: 'Frontend Architecture Lead', responsibility: 'Frontend feature-sliced structure', focus: 'Component library tiers' },
  { role: 'Database Architect', responsibility: 'Schema organization and migrations', focus: 'PostgreSQL schemas' },
  { role: 'AI Architecture Lead', responsibility: 'AI module placement', focus: 'prompt/dna/knowledge engines' },
  { role: 'DevOps Architect', responsibility: 'Infra and CI/CD folder layout', focus: 'Docker, deploy, monitoring' },
];

export const DEMO_ROOT: RootAreaRow[] = [
  { path: 'services/', purpose: 'Go microservices with cmd/ and internal/', owner: 'Backend Lead' },
  { path: 'pkg/', purpose: 'Shared domain-agnostic Go packages', owner: 'Platform Team' },
  { path: 'adapters/marketplace/', purpose: 'Marketplace-specific integration code', owner: 'Integration Lead' },
  { path: 'apps/web/', purpose: 'React TypeScript frontend application', owner: 'Frontend Lead' },
  { path: 'packages/ui/', purpose: 'Shared design system components', owner: 'Frontend Lead' },
  { path: 'infra/', purpose: 'Docker, nginx, deployment, monitoring', owner: 'DevOps Lead' },
  { path: 'docs/', purpose: 'Architecture, API, governance, ADR', owner: 'Documentation Architect' },
  { path: 'tests/', purpose: 'Cross-cutting integration, contract, E2E', owner: 'QA Architect' },
];

export const DEMO_BACKEND: BackendDomainRow[] = [
  { domain: 'identity', path: 'services/auth/', dependencies: 'pkg/authz', publicApi: 'POST /auth/login' },
  { domain: 'tenant', path: 'services/tenant/', dependencies: 'identity', publicApi: 'GET /tenants/{id}' },
  { domain: 'catalog', path: 'services/catalog/', dependencies: 'tenant, brand', publicApi: 'GET /products' },
  { domain: 'orders', path: 'services/orders/', dependencies: 'catalog, inventory', publicApi: 'POST /orders' },
  { domain: 'amazon', path: 'adapters/marketplace/amazon/', dependencies: 'catalog, credentials', publicApi: 'sync events' },
  { domain: 'ai-platform', path: 'services/aiplatform/', dependencies: 'knowledge, prompt', publicApi: 'POST /ai/infer' },
];

export const DEMO_FRONTEND: FrontendComponentRow[] = [
  { tier: 'Base', path: 'packages/ui/', examples: 'Button, Input, Card, Badge', consumers: 'all features' },
  { tier: 'Composite', path: 'apps/web/src/components/ui/', examples: 'KpiCard, DataTable, WorkspaceShell', consumers: 'pages, features' },
  { tier: 'Business', path: 'apps/web/src/components/', examples: 'AmazonGrowthPanel, BillingDashboard', consumers: 'domain pages' },
  { tier: 'AI', path: 'apps/web/src/components/ai/', examples: 'CopilotPanel, ReasoningPanel', consumers: 'AI features' },
  { tier: 'Marketplace', path: 'apps/web/src/pages/amazon/', examples: 'AmazonGrowthPage, InventoryIntelPage', consumers: 'marketplace routes' },
  { tier: 'Dashboard', path: 'apps/web/src/pages/', examples: 'DecisionIntelligenceDashboardPage', consumers: 'executive routes' },
];

export const DEMO_SCHEMAS: DatabaseSchemaRow[] = [
  { schema: 'identity', purpose: 'Users, roles, permissions, sessions', owner: 'Identity Team', tables: 12 },
  { schema: 'commerce', purpose: 'Products, SKUs, brands, categories', owner: 'Catalog Team', tables: 18 },
  { schema: 'inventory', purpose: 'Warehouses, stock levels, movements', owner: 'Supply Chain', tables: 8 },
  { schema: 'marketplace', purpose: 'Sync jobs, credentials, listings', owner: 'Integration Team', tables: 14 },
  { schema: 'ai', purpose: 'Prompts, models, inference logs', owner: 'AI Team', tables: 10 },
  { schema: 'audit', purpose: 'Audit trail, change log', owner: 'Platform Team', tables: 4 },
];

export const DEMO_AI_MARKETPLACE: AIMarketplaceRow[] = [
  { area: 'Prompt Engine', path: 'pkg/promptorch/', modules: 3, status: 'active' },
  { area: 'DNA Engine', path: 'pkg/branddna/', modules: 2, status: 'active' },
  { area: 'Knowledge Engine', path: 'services/knowledge/', modules: 4, status: 'active' },
  { area: 'Amazon Adapter', path: 'adapters/marketplace/amazon/', modules: 8, status: 'active' },
  { area: 'Shopify Adapter', path: 'adapters/marketplace/shopify/', modules: 6, status: 'planned' },
  { area: 'Marketplace Common', path: 'adapters/marketplace/common/', modules: 5, status: 'active' },
];

export const DEMO_SHARED_INFRA: SharedInfraRow[] = [
  { category: 'Shared Events', path: 'pkg/events/', items: 45, status: 'registered' },
  { category: 'Infrastructure', path: 'infra/deploy/', items: 12, status: 'active' },
  { category: 'Documentation', path: 'docs/', items: 120, status: 'current' },
  { category: 'Testing', path: 'tests/', items: 8, status: 'active' },
  { category: 'Tools', path: 'tools/', items: 6, status: 'active' },
  { category: 'Configuration', path: 'infra/config/', items: 4, status: 'environments' },
];

export const DEMO_EXECUTIVE: ExecutiveRepositoryRow[] = [
  { metric: 'Root Areas Defined', value: '13 top-level', status: 'complete', score: 100 },
  { metric: 'Backend Domains', value: '19 domains', status: 'mapped', score: 95 },
  { metric: 'PostgreSQL Schemas', value: '16 schemas', status: 'defined', score: 100 },
  { metric: 'Import Rules', value: '7 rules', status: 'enforced', score: 98 },
  { metric: 'Build Rules', value: '6 pipelines', status: 'active', score: 92 },
  { metric: '10-Year Evolution Ready', value: 'modular monolith', status: 'validated', score: 96 },
];

export function buildRepositoryDashboard() {
  return {
    team_roles: REPOSITORY_TEAM.length,
    root_areas: REPOSITORY_ROOT.length,
    backend_domains: BACKEND_DOMAINS.length,
    postgres_schemas: POSTGRES_SCHEMAS.length,
    shared_libraries: SHARED_LIBRARIES.length,
    governance_areas: IMPORT_RULES.length + BUILD_RULES.length + OWNERSHIP_FIELDS.length,
  };
}
